/* assets/gproperty-gcitypartners-bridge.js — GPROPERTY-GCP-BRIDGE-06
   Canonical narrow handoff owner: hydrate one matching GProperty local draft, one
   temporary LittleG spatial-entry context, and/or one opaque Family Free Report EV
   reference into the GCityPartners preliminary brief. GProperty keeps
   assets/gproperty-flow-context.js as the sole property draft/storage authority.
   LittleG spatial entry accepts only the typed session contract keyed by opaque SP-* id.
   Family report handoff accepts only opaque EV-* identity; report contents remain in
   the device-scoped D1/R2 report contract and are never placed in the URL.
   Never persists entry context automatically and never overrides a restored review draft. */
(function gpropertyGcitypartnersBridge(window, document) {
  'use strict';

  var STORAGE_KEY = 'gproperty_spatial_brief_draft_v2';
  var LEGACY_STORAGE_KEY = 'gproperty_spatial_brief_draft_v1';
  var SPATIAL_ENTRY_STORAGE_PREFIX = 'littleg_spatial_entry_v1:';
  var SPATIAL_ENTRY_SCHEMA = 'littleg_spatial_entry_context_v1';
  var SPATIAL_ENTRY_RESULT_CONTRACT = 'littleg_spatial_entry_result_v1';
  var ROOT_SELECTOR = '[data-gcitypartners-page-system]';
  var FORM_SELECTOR = '[data-enterprise-triage-form]';
  var VERSION = 'gproperty-gcp-bridge-07';
  var ALLOWED_SOURCES = {
    gproperty: true,
    'gproperty-map': true,
    'gproperty-assets': true,
    'gproperty-report': true,
    'gproperty-family-fit': true,
    'gproperty-owner': true,
    'gproperty-owner-fit': true
  };
  var ALLOWED_SPATIAL_DOMAINS = {
    property: true,
    survey: true,
    planning: true,
    valuation: true,
    unknown: true
  };
  var ALLOWED_SPATIAL_MODES = {
    default: true,
    map: true,
    report: true,
    '3d': true,
    ai_tool: true
  };
  var FIVE_PATH_ROUTES = {
    '/pages/gproperty': true,
    '/pages/surveying': true,
    '/pages/urban-renewal': true,
    '/pages/valuation-advisory': true,
    '/collections/glab': true
  };
  var STAGE_MAP = {
    exploring: 'early-review',
    'due-diligence': 'feasibility',
    planning: 'planning',
    procurement: 'delivery',
    'active-project': 'delivery',
    operations: 'operations'
  };
  var SPATIAL_HANDOFF_MAP = {
    property: { businessNeed: 'property-spatial-brief', serviceLane: 'property-assets-investment' },
    valuation: { businessNeed: 'property-spatial-brief', serviceLane: 'property-assets-investment' },
    survey: { businessNeed: 'spatial-survey-report', serviceLane: 'infrastructure-engineering' },
    planning: { businessNeed: 'enterprise-brief', serviceLane: 'urban-renewal' },
    unknown: { businessNeed: 'enterprise-brief', serviceLane: 'digital-city' }
  };

  function clean(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  }

  function clip(value, max) {
    return clean(value).slice(0, max || 500);
  }

  function opaque(value) {
    var text = clean(value);
    return /^[A-Za-z0-9._:-]{1,160}$/.test(text) ? text : '';
  }

  function opaqueSpatialEntry(value) {
    var text = clean(value);
    return /^SP-[A-Za-z0-9-]{8,80}$/.test(text) ? text : '';
  }

  function opaqueEvidence(value) {
    var text = clean(value);
    return /^EV-[A-Za-z0-9-]{8,80}$/.test(text) ? text : '';
  }

  function readDraft() {
    var gate = window.GPROPERTY_DATA_GATE;
    if (!gate || typeof gate.readPropertyDraft !== 'function') return null;
    var stored = gate.readPropertyDraft(STORAGE_KEY, LEGACY_STORAGE_KEY);
    return stored && stored.draft ? stored.draft : null;
  }

  function setIfEmpty(form, name, value) {
    var field = form && form.elements && form.elements.namedItem(name);
    if (field && !clean(field.value) && clean(value)) field.value = String(value);
  }

  function setOwnedValue(form, name, value) {
    var field = form && form.elements && form.elements.namedItem(name);
    if (field && clean(value)) field.value = String(value);
  }

  function localeStrippedPath(pathname) {
    return clean(pathname || '/')
      .replace(/^\/[a-z]{2,3}(?:-[a-z0-9]{2,8})*(?=\/)/i, '')
      .replace(/\/$/, '') || '/';
  }

  function safeSessionValue(key) {
    try {
      return window.sessionStorage.getItem(key) || '';
    } catch (_) {
      return '';
    }
  }

  function readSpatialEntry(entryId) {
    var safeId = opaqueSpatialEntry(entryId);
    if (!safeId) return null;
    var parsed = null;
    try {
      parsed = JSON.parse(safeSessionValue(SPATIAL_ENTRY_STORAGE_PREFIX + safeId) || 'null');
    } catch (_) {
      parsed = null;
    }
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.schema !== SPATIAL_ENTRY_SCHEMA) return null;
    if (opaqueSpatialEntry(parsed.entry_id) !== safeId) return null;
    if (parsed.result_contract !== SPATIAL_ENTRY_RESULT_CONTRACT) return null;
    if (!ALLOWED_SPATIAL_DOMAINS[clean(parsed.domain)]) return null;
    if (!ALLOWED_SPATIAL_MODES[clean(parsed.mode)]) return null;
    if (!Number.isFinite(Number(parsed.expires_at)) || Number(parsed.expires_at) <= Date.now()) return null;
    if (clean(parsed.evidence_state) !== 'entry_context_only') return null;

    var place = parsed.place && typeof parsed.place === 'object' ? parsed.place : {};
    var placeState = clean(place.state) === 'explicit' ? 'explicit' : 'unresolved';
    return {
      entryId: safeId,
      question: clip(parsed.question, 240),
      domain: clean(parsed.domain),
      mode: clean(parsed.mode),
      place: {
        state: placeState,
        text: placeState === 'explicit' ? clip(place.text, 160) : ''
      },
      createdAt: Number(parsed.created_at) || 0,
      expiresAt: Number(parsed.expires_at)
    };
  }

  function latestSpatialEntry() {
    var best = null;
    try {
      for (var index = 0; index < window.sessionStorage.length; index += 1) {
        var key = window.sessionStorage.key(index) || '';
        if (key.indexOf(SPATIAL_ENTRY_STORAGE_PREFIX) !== 0) continue;
        var entry = readSpatialEntry(key.slice(SPATIAL_ENTRY_STORAGE_PREFIX.length));
        if (!entry) continue;
        if (!best || entry.createdAt > best.createdAt) best = entry;
      }
    } catch (_) {
      return null;
    }
    return best;
  }

  function referrerContext() {
    try {
      if (!document.referrer) return { fivePath: false, entryId: '' };
      var url = new URL(document.referrer);
      if (url.origin !== window.location.origin) return { fivePath: false, entryId: '' };
      var path = localeStrippedPath(url.pathname);
      if (!FIVE_PATH_ROUTES[path]) return { fivePath: false, entryId: '' };
      return {
        fivePath: true,
        entryId: opaqueSpatialEntry(url.searchParams.get('entry_id'))
      };
    } catch (_) {
      return { fivePath: false, entryId: '' };
    }
  }

  function requestContext() {
    try {
      var search = new URLSearchParams(window.location.search || '');
      var source = clean(search.get('source')).toLowerCase();
      var recordId = ALLOWED_SOURCES[source] ? opaque(search.get('record_id')) : '';
      var directEntryId = opaqueSpatialEntry(search.get('entry_id'));
      var directEvidenceId = opaqueEvidence(search.get('evidence_id'));
      var referrer = referrerContext();
      var spatialEntry = readSpatialEntry(directEntryId || referrer.entryId);
      if (!spatialEntry && referrer.fivePath) spatialEntry = latestSpatialEntry();
      return {
        source: source,
        precheckType: clean(search.get('precheck')),
        recordId: recordId,
        evidenceId: directEvidenceId,
        spatialEntry: spatialEntry
      };
    } catch (_) {
      return null;
    }
  }

  function mappedStage(value) {
    var stage = clean(value);
    return STAGE_MAP[stage] || (
      stage === 'feasibility' || stage === 'delivery' ||
      stage === 'early-review' || stage === 'operations'
        ? stage
        : 'early-review'
    );
  }

  function referenceSummary(draft) {
    var references = draft && draft.fit_references && typeof draft.fit_references === 'object'
      ? draft.fit_references
      : {};
    var parts = [];
    ['family_fit', 'owner_fit'].forEach(function (key) {
      var value = references[key];
      if (!value || typeof value !== 'object') return;
      var resultId = opaque(value.result_id);
      if (!resultId) return;
      parts.push((key === 'family_fit' ? 'Family Fit' : 'Owner Fit') + ' Result ID: ' + resultId);
    });
    return parts.join('；');
  }

  function draftDescription(draft) {
    var direct = clean(draft.description || draft.observation);
    if (direct) return direct;
    var estate = draft.estate_context && typeof draft.estate_context === 'object'
      ? draft.estate_context
      : {};
    var location = clean(
      draft.project_location || estate.name_zh || estate.name_en || estate.address
    );
    var parts = [];
    if (location) parts.push('地點／物業：' + location);
    if (clean(draft.asset_type)) parts.push('資產類型：' + clean(draft.asset_type));
    if (clean(draft.business_need)) parts.push('目前需要：' + clean(draft.business_need));
    if (Array.isArray(draft.evidence_files) && draft.evidence_files.length) {
      parts.push('已有線索：' + draft.evidence_files.map(clean).filter(Boolean).join('、'));
    }
    var references = referenceSummary(draft);
    if (references) parts.push(references);
    return parts.join('。') + (parts.length ? '。' : '');
  }

  function spatialHandoff(entry) {
    var handoff = SPATIAL_HANDOFF_MAP[entry.domain] || SPATIAL_HANDOFF_MAP.unknown;
    if (entry.domain === 'unknown' && entry.mode === 'default') {
      handoff = { businessNeed: 'enterprise-brief', serviceLane: 'urban-renewal' };
    }
    return handoff;
  }

  function setBridgeStatus(root, message) {
    var status = root && root.querySelector('[data-gcp-review-status]');
    if (status) status.textContent = message;
  }

  function spatialStatusCopy(hasEvidence) {
    var lang = clean(document.documentElement.lang).toLowerCase();
    if (lang.indexOf('zh-cn') === 0 || lang.indexOf('zh-hans') === 0 || lang.indexOf('zh-sg') === 0) {
      return hasEvidence
        ? '已带入 LittleG 的问题脉络与免费报告 Evidence ID；请确认项目阶段与期望结果后，再保存本机摘要。尚未提交给 GLG。'
        : '已带入 LittleG 的问题脉络；请确认项目阶段与期望结果后，再保存本机摘要。尚未提交给 GLG。';
    }
    if (lang.indexOf('zh') === 0) {
      return hasEvidence
        ? '已帶入 LittleG 嘅問題脈絡同免費報告 Evidence ID；請確認項目階段同期望結果後，再保存本機摘要。尚未提交畀 GLG。'
        : '已帶入 LittleG 的問題脈絡；請確認項目階段與期望結果後，再保存本機摘要。尚未提交給 GLG。';
    }
    return hasEvidence
      ? 'LittleG question context and the free-report Evidence ID were loaded. Confirm the project stage and desired outcome before saving the local brief. Nothing has been submitted to GLG.'
      : 'LittleG question context loaded. Confirm the project stage and desired outcome before saving the local brief. Nothing has been submitted to GLG.';
  }

  function preserveRestoredDraft(root) {
    if (root.getAttribute('data-review-brief-state') !== 'local-draft-restored') return false;
    root.setAttribute('data-gproperty-bridge-version', VERSION);
    root.setAttribute('data-gproperty-bridge-state', 'review-draft-preserved');
    return true;
  }

  function hydrateProperty(root, form, context) {
    if (!context.recordId) return false;
    var draft = readDraft();
    if (!draft || typeof draft !== 'object') return false;
    var draftRecord = opaque(draft.record_id);
    if (!draftRecord || draftRecord !== context.recordId) return false;

    var estate = draft.estate_context && typeof draft.estate_context === 'object'
      ? draft.estate_context
      : {};
    setIfEmpty(
      form,
      'project_location',
      draft.project_location || estate.name_zh || estate.name_en || estate.address
    );
    setIfEmpty(form, 'project_stage', mappedStage(draft.project_stage));
    setOwnedValue(form, 'business_need', 'property-spatial-brief');
    setOwnedValue(form, 'service_lane', 'property-assets-investment');
    setIfEmpty(form, 'description', draftDescription(draft));
    if (context.evidenceId) setIfEmpty(form, 'evidence_files', 'LittleG Evidence ID: ' + context.evidenceId);

    root.setAttribute('data-gproperty-bridge-state', 'local-draft-hydrated');
    root.setAttribute('data-gproperty-bridge-record-id', context.recordId);
    root.setAttribute('data-gproperty-bridge-source', context.source);
    if (context.evidenceId) root.setAttribute('data-family-free-report-evidence-id', context.evidenceId);
    return true;
  }

  function hydrateSpatialEntry(root, form, entry, evidenceId) {
    if (!entry) return false;
    var handoff = spatialHandoff(entry);
    setOwnedValue(form, 'source', evidenceId ? 'family-free-report' : 'littleg-spatial-entry');
    if (entry.place.state === 'explicit') setIfEmpty(form, 'project_location', entry.place.text);
    setOwnedValue(form, 'business_need', handoff.businessNeed);
    setOwnedValue(form, 'service_lane', handoff.serviceLane);
    setIfEmpty(form, 'description', entry.question);
    if (evidenceId) setIfEmpty(form, 'evidence_files', 'LittleG Evidence ID: ' + evidenceId);

    root.setAttribute('data-gproperty-bridge-state', 'spatial-entry-hydrated');
    root.setAttribute('data-spatial-entry-bridge-version', VERSION);
    root.setAttribute('data-spatial-entry-id', entry.entryId);
    root.setAttribute('data-spatial-entry-domain', entry.domain);
    root.setAttribute('data-spatial-entry-mode', entry.mode);
    root.setAttribute('data-spatial-entry-place-state', entry.place.state);
    if (evidenceId) root.setAttribute('data-family-free-report-evidence-id', evidenceId);
    setBridgeStatus(root, spatialStatusCopy(Boolean(evidenceId)));
    return true;
  }

  function hydrateEvidenceReference(root, form, evidenceId) {
    if (!evidenceId) return false;
    setOwnedValue(form, 'source', 'family-free-report');
    setOwnedValue(form, 'business_need', 'property-spatial-brief');
    setOwnedValue(form, 'service_lane', 'property-assets-investment');
    setIfEmpty(form, 'evidence_files', 'LittleG Evidence ID: ' + evidenceId);
    root.setAttribute('data-gproperty-bridge-state', 'family-report-evidence-hydrated');
    root.setAttribute('data-family-free-report-evidence-id', evidenceId);
    setBridgeStatus(root, spatialStatusCopy(true));
    return true;
  }

  function hydrate(root) {
    if (!root || root.getAttribute('data-gproperty-bridge-version') === VERSION) return;
    var context = requestContext();
    if (!context) return;
    var form = root.querySelector(FORM_SELECTOR);
    if (!form) return;

    if (preserveRestoredDraft(root)) return;
    var precheck = form.elements && form.elements.namedItem('precheck_type');
    if (context.precheckType === 'uav-3d' || clean(precheck && precheck.value) === 'uav-3d') {
      root.setAttribute('data-gproperty-bridge-version', VERSION);
      root.setAttribute('data-gproperty-bridge-state', 'uav-precheck-preserved');
      return;
    }

    var hydrated = hydrateProperty(root, form, context);
    if (!hydrated) hydrated = hydrateSpatialEntry(root, form, context.spatialEntry, context.evidenceId);
    if (!hydrated) hydrated = hydrateEvidenceReference(root, form, context.evidenceId);
    if (!hydrated) return;

    root.setAttribute('data-gproperty-bridge-version', VERSION);
  }

  function boot(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) hydrate(target);
    if (target.querySelectorAll) {
      Array.prototype.slice.call(target.querySelectorAll(ROOT_SELECTOR)).forEach(hydrate);
    }
  }

  if (!window.__gpropertyBridgeLifecycleInstalled) {
    window.__gpropertyBridgeLifecycleInstalled = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
    }
    document.addEventListener('shopify:section:load', function (event) {
      boot(event.target || document);
    });
  }
  if (document.readyState !== 'loading') boot(document);
})(window, document);
